import { Tables, putItem, scanAll, getItem, updateItem } from "./db";
import type { FeatureRequest, ItemStatus, ItemPriority } from "../types";

/** Get all feature requests */
export async function getAllFeatures(): Promise<FeatureRequest[]> {
  const items = await scanAll(Tables.FEATURES);
  return (items as FeatureRequest[]).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/** Get a single feature request */
export async function getFeature(id: string): Promise<FeatureRequest | undefined> {
  const item = await getItem(Tables.FEATURES, { id });
  return item as FeatureRequest | undefined;
}

/** Create a new feature request */
export async function createFeature(data: {
  title: string;
  description: string;
  priority: ItemPriority;
  author: string;
}): Promise<FeatureRequest> {
  const feature: FeatureRequest = {
    id: `fr-${Date.now()}`,
    title: data.title,
    description: data.description,
    status: "open",
    priority: data.priority,
    createdAt: new Date().toISOString(),
    linkedCommits: [],
    author: data.author,
  };

  await putItem(Tables.FEATURES, feature);
  return feature;
}

/** Update a feature request */
export async function updateFeature(
  id: string,
  updates: Partial<Pick<FeatureRequest, "title" | "description" | "status" | "priority" | "completedInVersion" | "linkedCommits">>
): Promise<FeatureRequest | undefined> {
  const existing = await getFeature(id);
  if (!existing) return undefined;

  const merged = { ...updates, updatedAt: new Date().toISOString() };
  await updateItem(Tables.FEATURES, { id }, merged);
  return { ...existing, ...merged };
}

/** Link a commit to a feature */
export async function linkCommitToFeature(featureId: string, commitId: string): Promise<void> {
  const feature = await getFeature(featureId);
  if (!feature) return;

  if (!feature.linkedCommits.includes(commitId)) {
    await updateItem(Tables.FEATURES, { id: featureId }, {
      linkedCommits: [...feature.linkedCommits, commitId],
      updatedAt: new Date().toISOString(),
    });
  }
}

/** Mark a feature as completed in a version */
export async function completeFeatureInVersion(featureId: string, versionId: string): Promise<void> {
  await updateItem(Tables.FEATURES, { id: featureId }, {
    status: "completed" as ItemStatus,
    completedInVersion: versionId,
    updatedAt: new Date().toISOString(),
  });
}
